import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

const useMessages = (props: any) => {
    const { messages, totalPages, onPaginate } = props;

    const memoizedMessages = useMemo(() => messages, [messages]);

    // local states
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoadingNewMessages, setIsLoadingNewMessages] = useState(false);
    const [scrollHeight, setScrollHeight] = useState<number>(0);
    const [scrollPosition, setScrollPosition] = useState<number>(0);
    const [isMessageError, setIsMessageError] = useState<boolean>(false);
    const [newestMessageId, setNewestMessageId] = useState<number>(
        memoizedMessages[0].id,
    );

    // scroll to bottom when new message sent
    useEffect(() => {
        if (newestMessageId !== memoizedMessages[0].id)
            handleScrollToPosition(scrollHeight);
    }, [memoizedMessages]);

    // refrences
    const containerRef = useRef<HTMLDivElement>(null);

    // handle pagination for loading older messages
    useEffect(() => {
        const isValidPage = currentPage > 1 && currentPage <= totalPages;

        if (isValidPage && onPaginate) {
            onPaginate(currentPage);
        }
    }, [currentPage, totalPages, onPaginate]);

    // trigger the scrolling action when it gets to top of container
    const onScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            const { target } = event;

            if (
                target instanceof HTMLDivElement &&
                target.scrollTop === 0 &&
                !isLoadingNewMessages &&
                currentPage < totalPages
            ) {
                setNewestMessageId(memoizedMessages[0].id);
                setCurrentPage((prevPage) => prevPage + 1);
                setIsLoadingNewMessages(true);
            }
        },
        [isLoadingNewMessages, currentPage, totalPages],
    );

    // get the height of the scrolling area and handling after load message
    useEffect(() => {
        const divElement = document.getElementById('messagesContainer');

        if (divElement) {
            const newScrollHeight = divElement.scrollHeight;

            if (isLoadingNewMessages && newScrollHeight !== scrollHeight) {
                const scrollDifference = newScrollHeight - (scrollHeight + 60);
                handleScrollToPosition(scrollDifference);
                setScrollHeight(newScrollHeight);
                setIsLoadingNewMessages(false);
            } else {
                setScrollHeight(newScrollHeight);
            }
        }
    }, [memoizedMessages]);

    // handle to the latest message on load
    useEffect(() => {
        if (scrollHeight && !scrollPosition) {
            handleScrollToPosition(scrollHeight);
        }
    }, [scrollHeight, scrollPosition]);

    // handle scroll to the last message of new loadings
    const handleScrollToPosition = (position: number) => {
        const divElement = containerRef.current;

        if (divElement) {
            divElement.scrollTop = position;
            setScrollPosition(position);
        }
    };

    // for handling the avatars initials
    const onStringAvatar = useCallback(
        (name: string, family: string | undefined) => {
            return {
                children: `${name?.trim()?.[0] ?? family?.trim()?.[0] ?? 'ک'}`,
            };
        },
        [],
    );

    // handle get file name out of url
    const getFileNameFromURL = (file: any): string | undefined => {
        if (!file) return 'فایل';

        const lastDotIndex = file?.lastIndexOf('.');
        const secondToLastDotIndex = file?.lastIndexOf('.', lastDotIndex - 1);
        const firstSlashIndex = file?.lastIndexOf('/');

        if (secondToLastDotIndex >= 0 && firstSlashIndex >= 0) {
            const extractedString = file.substring(
                secondToLastDotIndex,
                firstSlashIndex + 1,
            );
            return extractedString;
        } else {
            const dotPositions = [];
            let dotIndex = file?.indexOf('.');

            while (dotIndex !== -1) {
                dotPositions.push(dotIndex);
                dotIndex = file?.indexOf('.', dotIndex + 1);
            }

            if (dotPositions.length >= 2) {
                const secondToLastDotIndex =
                    dotPositions[dotPositions.length - 2];
                const extractedString = file?.substring(
                    0,
                    secondToLastDotIndex,
                );
                return extractedString;
            } else {
                return file;
            }
        }
    };

    // take out the format out of text
    const handleGetFileFormat = (file: string) => {
        var lastDotIndex = file?.lastIndexOf('.');

        if (lastDotIndex !== -1) {
            var result = file?.substring(lastDotIndex + 1);

            return result;
        } else {
            return file;
        }
    };

    // handle download any file
    const handleDownloadFile = (file: any) => {
        const fileUrl = file?.file ?? file;

        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = '';
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    //    *** more safe version
    // const handleDownloadFile = async (file: any) => {
    //     console.log('file', file);

    //     try {
    //         // Get the actual file URL
    //         const fileUrl = file?.file ?? file;

    //         if (!fileUrl) {
    //             throw new Error('Invalid file URL');
    //         }

    //         // Extract filename from URL or fallback to 'downloaded-file'
    //         const getFileNameFromURL = (url: string) => {
    //             try {
    //                 return (
    //                     decodeURIComponent(url.split('/').pop() || '') ||
    //                     'downloaded-file'
    //                 );
    //             } catch {
    //                 return 'downloaded-file';
    //             }
    //         };

    //         const fileName = getFileNameFromURL(fileUrl);

    //         // Fetch the file as a blob
    //         const response = await fetch(fileUrl);
    //         if (!response.ok) {
    //             throw new Error(
    //                 `Failed to fetch file: ${response.status} ${response.statusText}`,
    //             );
    //         }

    //         const blob = await response.blob();

    //         // Create a downloadable link
    //         const href = window.URL.createObjectURL(blob);
    //         const anchor = document.createElement('a');
    //         anchor.href = href;
    //         anchor.download = fileName;
    //         anchor.style.display = 'none';

    //         document.body.appendChild(anchor);
    //         anchor.click();

    //         // Cleanup
    //         document.body.removeChild(anchor);
    //         window.URL.revokeObjectURL(href);
    //     } catch (error) {
    //         console.error('Error downloading file:', error);
    //     }
    // };

    // update isMessage error from props
    useEffect(() => {
        props?.isMessageError
            ? setIsMessageError(true)
            : setIsMessageError(false);
    }, [props?.isMessageError]);

    return {
        isLoadingNewMessages,
        scrollPosition,
        containerRef,
        onScroll,
        onStringAvatar,
        getFileNameFromURL,
        messages,
        userInfo: props?.userInfo,
        isSending: props?.isSending,
        isRestoreDelete: props?.isRestoreDelete,
        avatarPrimaryRoute: props?.avatarPrimaryRoute,
        avatarSecondaryRoute: props?.avatarSecondaryRoute,
        isMessageError: isMessageError,

        onGetFileFormat: handleGetFileFormat,
        onDownloadFile: handleDownloadFile,
        onDeleteOrRestore: props?.onDeleteOrRestore,
        onEdit: props?.onEdit,
        onReply: props?.onReply,
        gatewayOwnerId: props?.gatewayOwnerId,
        gatewayName: props?.gatewayName,
    };
};

export default useMessages;
